import React from 'react'

import { Stack } from '@mui/material'

import { InfoTooltip } from 'components/feedback/InfoTooltip'

/**
 * A DataGrid column header that carries a literal explanation.
 *
 * ## The sibling of `headerWithTerm`, and the split between them is deliberate
 *
 * `headerWithTerm(label, termId)` is for a header explaining a TERM OF ART, and it
 * offers no way to pass prose precisely so the definition stays in `docs/glossary.json`
 * where every other surface reads the same sentence (§44). Its own docblock names the
 * escape hatch this file is: *"If a header genuinely needs to explain a CONTROL rather
 * than a term, use `InfoTooltip` with `text` directly and do not reach for this."*
 *
 * That instruction was correct and had nowhere to land. The only header-with-prose in
 * the app is a local closure inside `TagInventoryPage` built on a raw MUI `Tooltip`,
 * which `headerWithTerm`'s docblock already flags as "a third hand-roll waiting to
 * happen" (§36) — and being a raw `Tooltip` it gets none of `InfoTooltip`'s behaviour.
 * The signups grid is that third hand-roll arriving, so the shared helper is built
 * instead (§36: extract on the second, §47: improve the base rather than beside it).
 *
 * ## When prose is the RIGHT answer and a term is not
 *
 * The body here is not a definition — it is a statement about THIS COLUMN'S DATA that
 * no glossary entry could carry: what the seeded default was, what unit the number is
 * in, what the source system does or does not send. `websiteColumns.tsx` composes these
 * per field from the manifest, so the sentence is generated rather than typed, which is
 * the same drift protection a glossary id buys — just anchored to a different source of
 * truth. If what you are about to write is a definition of a word, use `headerWithTerm`.
 *
 * ## ⚠️ Adding this to a column WIDENS its minimum width
 *
 * The glyph is `INFO_GLYPH_SIZE` (14px) plus `InfoTooltip`'s own 4px left margin, so a
 * header costs **18px more** than its text alone. §49 (a column header is never
 * truncated, and a number column is sized by its HEADER not its values) therefore
 * applies to the width you author, and the extra has to be in it — see
 * `INFO_HEADER_ALLOWANCE`, which exists so a caller adds the same number every time
 * rather than each re-estimating it.
 *
 * Usage:
 *
 *     { field: 'units', headerName: 'Units per year', width: 150 + INFO_HEADER_ALLOWANCE,
 *       renderHeader: headerWithInfo('Units per year', 'Website default: 25,000.') }
 *
 * `headerName` is still passed because the grid uses it for the column menu, the
 * column-visibility panel and CSV export, none of which render `renderHeader`.
 */
export const headerWithInfo =
  (label: string, text: React.ReactNode) =>
  (): React.ReactElement => (
    <Stack direction="row" alignItems="center" sx={{ minWidth: 0 }}>
      {/*
        Plain text, not a `Typography`: the grid's own header cell already applies the
        column's font and overflow rules to this subtree, and re-declaring them here is
        how a tooltipped header starts sitting differently from the bare ones beside it.
        Same reasoning, same markup, as `headerWithTerm`.
      */}
      <span>{label}</span>
      {/*
        No `spacing` on the Stack — `InfoTooltip` supplies its own 4px left margin on the
        default glyph. Adding one would double the gap and invalidate the width allowance
        below, which is measured against exactly this arrangement.
      */}
      <InfoTooltip text={text} />
    </Stack>
  )

/**
 * The px a column must gain to fit an info glyph in its header without truncating (§49).
 *
 * 14px glyph + 4px margin. A constant rather than a number retyped per column, because
 * the failure it prevents is invisible at authoring time and only shows up as `Uncertif…`
 * on somebody else's screen.
 */
export const INFO_HEADER_ALLOWANCE = 18
